import { motion } from "framer-motion";
import { Target, Eye, Heart, Shield, Award, Users, Truck, CheckCircle2 } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";

const values = [
  { icon: Shield, title: "Quality First", desc: "WHO-GMP compliant manufacturing with rigorous quality control at every stage." },
  { icon: Heart, title: "Patient Centric", desc: "Every product is designed with the patient's well-being as the ultimate priority." },
  { icon: Award, title: "Innovation", desc: "Continuously advancing our formulations to meet evolving healthcare needs." },
  { icon: Users, title: "Accessibility", desc: "Making premium pharmaceuticals affordable and accessible to all." },
  { icon: Truck, title: "Reliability", desc: "Consistent supply chain ensuring products reach healthcare providers on time." },
  { icon: CheckCircle2, title: "Ethics", desc: "Operating with transparency, integrity, and respect for all stakeholders." },
];

const milestones = [
  { year: "Foundation", desc: "Drukst Biotech was established in Gujarat with a vision to transform pharmaceutical care across India." },
  { year: "Growth", desc: "Expanded product portfolio to 500+ pharmaceutical products across 6 categories and multiple therapeutic areas." },
  { year: "Innovation", desc: "Launched AI-powered drug knowledge platform and comprehensive product ranges for modern healthcare needs." },
  { year: "Today", desc: "Pan-India distribution from our corporate headquarters in Himatnagar, Gujarat, serving hospitals and clinics nationwide." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="min-h-screen pt-16 md:pt-0">
      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-semibold tracking-[0.2em] text-[#2DD4BF] uppercase mb-4">About Us</p>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-[1.08] max-w-3xl">
              The Intersection of<br />
              <span className="gradient-text">Biology & Technology</span>
            </h1>
            <p className="text-[#B0BEC5] text-base md:text-lg max-w-2xl leading-[1.75]">
              Drukst Biotech Pvt. Ltd. is a trusted name in pharmaceuticals, committed to delivering
              high-quality, affordable healthcare solutions that improve patient outcomes across India.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-6" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          <ScrollReveal>
            <div className="p-8 md:p-10 rounded-xl border border-white/5 bg-white/[0.02] h-full">
              <div className="w-12 h-12 rounded-lg bg-[#2DD4BF]/10 flex items-center justify-center mb-6">
                <Target size={24} className="text-[#2DD4BF]" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Our Mission</h3>
              <p className="text-[#B0BEC5] leading-[1.8] text-[15px]">
                To provide reliable, trusted, and affordable pharmaceutical solutions,
                ensuring the safety and well-being of every patient. We strive to bridge the gap between
                advanced healthcare and accessibility for communities across India.
              </p>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <div className="p-8 md:p-10 rounded-xl border border-white/5 bg-white/[0.02] h-full">
              <div className="w-12 h-12 rounded-lg bg-[#3B82F6]/10 flex items-center justify-center mb-6">
                <Eye size={24} className="text-[#3B82F6]" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Our Vision</h3>
              <p className="text-[#B0BEC5] leading-[1.8] text-[15px]">
                To become a globally recognized pharmaceutical company known for innovation, quality,
                and commitment to patient care. We envision a future where premium healthcare is
                available to every individual, regardless of geography.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Divider */}
      <div className="section-line max-w-7xl mx-auto" />

      {/* Values */}
      <section className="py-20 px-6" data-testid="values-section">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-16">
              <p className="text-xs font-semibold tracking-[0.2em] text-[#2DD4BF] uppercase mb-3">What Drives Us</p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white">Our Core Values</h2>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {values.map((v, i) => (
              <ScrollReveal key={v.title} delay={i * 60}>
                <div
                  data-testid={`value-${v.title.toLowerCase().replace(/\s/g,'-')}`}
                  className="group p-7 rounded-xl border border-white/5 bg-white/[0.01] hover:border-white/10 transition-all duration-300"
                >
                  <v.icon size={22} className="text-[#2DD4BF] mb-4 group-hover:scale-110 transition-transform" />
                  <h4 className="text-base font-bold text-white mb-2">{v.title}</h4>
                  <p className="text-sm text-[#B0BEC5] leading-[1.75]">{v.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="section-line max-w-7xl mx-auto" />

      {/* Journey */}
      <section className="py-20 px-6" data-testid="journey-section">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="mb-16">
              <p className="text-xs font-semibold tracking-[0.2em] text-[#2DD4BF] uppercase mb-3">Our Journey</p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white">Building the Future of Healthcare</h2>
            </div>
          </ScrollReveal>

          <div className="relative">
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-white/10" />
            {milestones.map((m, i) => (
              <ScrollReveal key={m.year} delay={i * 100}>
                <div className={`relative flex items-start gap-8 mb-12 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                  <div className="absolute left-6 md:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[#2DD4BF] ring-4 ring-[#020617] z-10" />
                  <div className={`ml-14 md:ml-0 md:w-[45%] ${i % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12"}`}>
                    <span className="text-sm font-bold text-[#2DD4BF]">{m.year}</span>
                    <p className="text-[#B0BEC5] text-sm mt-2 leading-[1.75]">{m.desc}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Image section */}
      <section className="py-16 px-6 pb-32">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="relative rounded-2xl overflow-hidden aspect-[2.5/1] border border-white/5">
              <img
                src="https://images.unsplash.com/photo-1558460733-1d382078bf52?auto=format&fit=crop&w=1400&q=80"
                alt="Pharmaceutical research"
                className="w-full h-full object-cover opacity-50"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#020617] via-[#020617]/60 to-transparent" />
              <div className="absolute inset-0 flex items-center px-8 md:px-16">
                <div className="max-w-lg">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">Your Health is Our Concern</h3>
                  <p className="text-[15px] text-[#B0BEC5] leading-[1.7]">
                    Where innovation meets care, lies Drukst Biotech. Delivering trusted pharmaceutical solutions from Gujarat to the world.
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
